import { useState } from 'react';
import { Card } from '../components/Card';
import { PageHeader } from '../components/admin/AdminUI';
import { authFetchWithRecovery } from '../utils/sessionRecovery';

/**
 * Admin console: verified email change for PLATFORM-INTERNAL accounts.
 *
 * The page never sees a token, a hash or a password. It looks a target up by
 * its CURRENT email (to show its id and eligibility), starts a change (the
 * verification goes to the NEW address; the change is not made here), lists the
 * account's change history and cancels an open request. The backend enforces
 * every rule; this page only presents them.
 */
const REASONS = [
  { value: 'suite_identity_migration', label: 'Suite identity migration' },
  { value: 'mailbox_retired', label: 'Mailbox retired' },
  { value: 'address_correction', label: 'Address correction' },
] as const;

interface Eligibility {
  userId: string;
  isPlatformInternal: boolean;
  isCentralAdmin: boolean;
  eligible: boolean;
  ineligibleReason: string | null;
}

interface ChangeRequest {
  id: string;
  oldEmail: string;
  newEmail: string;
  reason: string;
  status: string;
  createdAt: string;
  expiresAt: string;
}

async function adminFetch(path: string, init: RequestInit = {}) {
  // Renews an aged-out admin token from the Central session (as the directory
  // pages do) instead of failing with a bare 401.
  const { response: res } = await authFetchWithRecovery(`/api/admin${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json' },
  });
  if (res === null) throw new Error('Your session ended. Signing you in again…');
  const body = await res.json().catch(() => ({}));
  if (res.status === 404 && (!body.error || body.error === 'Not found')) {
    throw new Error('Verified email change is switched off in this environment.');
  }
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

export function UserEmailChangePage() {
  const [currentEmail, setCurrentEmail] = useState('');
  const [target, setTarget] = useState<Eligibility | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [reason, setReason] = useState<string>(REASONS[0].value);
  const [history, setHistory] = useState<ChangeRequest[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = async (userId: string) => {
    const body = await adminFetch(`/users/${userId}/email-change`);
    setHistory(body.requests ?? []);
  };

  const lookup = () =>
    run(async () => {
      setTarget(null);
      setHistory([]);
      const body = await adminFetch(
        `/users/email-change-eligibility?email=${encodeURIComponent(currentEmail.trim())}`
      );
      setTarget(body);
      await loadHistory(body.userId);
    });

  const submit = () =>
    run(async () => {
      if (!target) return;
      const body = await adminFetch(`/users/${target.userId}/email-change`, {
        method: 'POST',
        body: JSON.stringify({ newEmail: newEmail.trim(), reason }),
      });
      setMessage(
        `Verification sent to the new address. Old-address notice: ${body.oldAddressNotice}. ` +
          'The change takes effect only when the new address confirms.'
      );
      await loadHistory(target.userId);
    });

  const cancel = (requestId: string) =>
    run(async () => {
      if (!target) return;
      await adminFetch(`/users/${target.userId}/email-change/${requestId}/cancel`, { method: 'POST' });
      setMessage('Request cancelled.');
      await loadHistory(target.userId);
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Verified email change"
        subtitle={<>Platform-internal accounts only. The new address must confirm before it becomes the sign-in
          email; the account keeps its id, memberships and access, and is signed out everywhere.</>}
      />

      <Card title="1. Find the account by its current sign-in email">
        <div className="flex gap-2">
          <input
            aria-label="Current email"
            value={currentEmail}
            onChange={(e) => setCurrentEmail(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
            placeholder="current address"
          />
          <button
            type="button"
            onClick={lookup}
            disabled={busy || !currentEmail.trim()}
            className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            Look up
          </button>
        </div>
        {target && (
          <dl className="mt-3 text-sm grid grid-cols-2 gap-1" data-testid="email-change-target">
            <dt className="text-slate-500">User id</dt>
            <dd className="font-mono">{target.userId}</dd>
            <dt className="text-slate-500">Platform-internal</dt>
            <dd>{target.isPlatformInternal ? 'yes' : 'no'}</dd>
            <dt className="text-slate-500">Eligible</dt>
            <dd>{target.eligible ? 'yes' : `no (${target.ineligibleReason})`}</dd>
          </dl>
        )}
      </Card>

      {target?.eligible && (
        <Card title="2. Request the change">
          <div className="flex flex-col gap-2 max-w-md">
            <input
              aria-label="New email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
              placeholder="new company-controlled address"
            />
            <select
              aria-label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !newEmail.trim()}
              className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              Send verification to new address
            </button>
          </div>
        </Card>
      )}

      {message && <p className="text-sm text-green-700" data-testid="email-change-message">{message}</p>}
      {error && <p className="text-sm text-red-600" data-testid="email-change-error">{error}</p>}

      {target && (
        <Card title="History">
          {history.length === 0 ? (
            <p className="text-sm text-slate-500">No email-change requests for this account.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="py-1">Created</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="py-1">{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.oldEmail}</td>
                    <td>{r.newEmail}</td>
                    <td>{r.reason}</td>
                    <td>{r.status}</td>
                    <td>
                      {r.status === 'REQUESTED' && (
                        <button
                          type="button"
                          onClick={() => cancel(r.id)}
                          disabled={busy}
                          className="text-red-600 hover:underline"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
